// frontend-react/src/pages/ContractComparisonPage.tsx
import React, { useState, useCallback } from 'react';
import { Layout, Card, Button, Upload, Space, Progress, Spin, Modal, message } from 'antd';
import {
  InboxOutlined,
  HistoryOutlined,
  DownloadOutlined,
  SaveOutlined,
  DiffOutlined,
  FileWordOutlined,
} from '@ant-design/icons';

import apiClient from '../services/apiClient';

import PageLayout from '../components/PageLayout';
import SystemStatus from '../components/SystemStatus';
import DisclaimerBanner from '../components/DisclaimerBanner';
import PDFViewer from '../components/PDFViewer';
import ReviewResults from '../components/ReviewResults';
import ReviewHistoryWithShare from '../components/ReviewHistoryWithShare';

import RuleUploadModal from '../components/RuleManagement/RuleUploadModal';
import RuleSelector from '../components/RuleManagement/RuleSelector';
import DepartmentAssignModal from '../components/RuleManagement/DepartmentAssignModal';
import RulePreviewModal from '../components/RuleManagement/RulePreviewModal';

import { useAdReviewController } from './adReview/useAdReviewController';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const { Content } = Layout;
const { Dragger } = Upload;

const REVIEW_PANEL_HEIGHT = 'max(700px, calc(100vh - 280px))';

const ContractComparisonPage: React.FC = () => {
  const c = useAdReviewController();
  const { riskLevelEnabled } = useFeatureFlags();
  const [pdfScale, setPdfScale] = useState(1.0);
  const [isDownloadingTrackChanges, setIsDownloadingTrackChanges] = useState(false);
  const handlePdfScaleChange = useCallback((s: number) => setPdfScale(s), []);

  // ── 純修訂記錄 Modal ──
  const [trackChangesModalVisible, setTrackChangesModalVisible] = useState(false);
  const [tcOriginalFile, setTcOriginalFile] = useState<File | null>(null);
  const [tcModifiedFile, setTcModifiedFile] = useState<File | null>(null);
  const [isGeneratingTrackChanges, setIsGeneratingTrackChanges] = useState(false);

  const handleGenerateTrackChanges = useCallback(async () => {
    if (!tcOriginalFile || !tcModifiedFile) {
      message.warning('請上傳原稿與修改版');
      return;
    }
    setIsGeneratingTrackChanges(true);
    try {
      const formData = new FormData();
      formData.append('original_file', tcOriginalFile);
      formData.append('modified_file', tcModifiedFile);

      const resp = await apiClient.post('/api/v1/comparison/generate-track-changes', formData, {
        responseType: 'blob',
      });

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stem = tcModifiedFile.name.replace(/\.(docx|pdf)$/i, '');
      a.download = `tracked_${stem}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('修訂記錄已下載');
      setTrackChangesModalVisible(false);
      setTcOriginalFile(null);
      setTcModifiedFile(null);
    } catch (err: any) {
      console.error('產生修訂記錄失敗:', err);
      message.error('產生修訂記錄失敗');
    } finally {
      setIsGeneratingTrackChanges(false);
    }
  }, [tcOriginalFile, tcModifiedFile]);

  const handleDownloadTrackChanges = useCallback(async () => {
    if (!c.uploadedFile || !c.ruleSetId) {
      message.warning('請先選擇規則集並上傳待比對文件');
      return;
    }
    setIsDownloadingTrackChanges(true);
    try {
      const formData = new FormData();
      formData.append('file', c.uploadedFile);
      formData.append('rule_set_id', String(c.ruleSetId));
      if (c.activeResults.length > 0) {
        formData.append('results_json', JSON.stringify(c.activeResults));
      }

      const resp = await apiClient.post('/api/v1/comparison/download-track-changes', formData, {
        responseType: 'blob',
      });

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stem = c.uploadedFile.name.replace(/\.(docx|pdf)$/i, '');
      a.download = `tracked_${stem}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('修訂記錄已下載');
    } catch (err: any) {
      console.error('下載修訂記錄失敗:', err);
      message.error('下載修訂記錄失敗');
    } finally {
      setIsDownloadingTrackChanges(false);
    }
  }, [c.uploadedFile, c.ruleSetId]);

  const pdfColumnFr = Math.max(1, Math.round(pdfScale * 4));
  const reviewColumnFr = 4;
  const gridColumns =
    c.pdfData || c.isLoadingHistoryFile || c.isReviewing
      ? `${pdfColumnFr}fr ${reviewColumnFr}fr`
      : '1fr';

  const showingResultPanel = !!(
    c.pdfData ||
    c.isLoadingHistoryFile ||
    c.reviewResults.length > 0 ||
    c.isReviewing
  );

  return (
    <PageLayout currentPage='/contract-comparison'>
      {/* 副標題區域 */}
      <div
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--color-text)' }}>合約比對系統</h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280' }}>
            ① 上傳標準合約(建立規則) → ② 選擇檔名 → ③ 上傳修改後文件 → ④「開始比對」→ ⑤ 下載修訂紀錄(含 AI 註解)
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SystemStatus />
        </div>
      </div>

      <Content
        style={{
          padding: '24px',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          background: 'var(--color-bg)',
        }}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          {/* 規則選擇區域 */}
          {!showingResultPanel && (
            <RuleSelector
              ruleSets={c.ruleSets}
              manageRuleSets={c.manageRuleSets}
              selectedRuleSetId={c.ruleSetId}
              onRuleSetChange={c.setRuleSetId}
              selectedValue={c.selectRuleValue}
              onValueChange={c.handleRuleSelectChange}
              onUploadClick={c.openUploadRuleModal}
              onShareClick={c.openShareModal}
              onPreviewClick={c.openPreviewModal}
              onRefresh={c.loadRuleSets}
              isLoading={c.isLoadingRules}
              canUploadRules={true}
              canShareRules={c.canShareRules}
              isAdmin={c.isAdmin}
              isSuperAdmin={c.isSuperAdmin}
              userId={c.user?.id}
              isComparisonPage
              userPreferredRuleSetId={c.user?.preferred_rule_set_id ?? null}
              onSetUserPreferred={c.handleSetUserPreferred}
            />
          )}

          {/* Upload Section */}
          {!showingResultPanel && (
            <>
              <Dragger
                name='file'
                multiple={false}
                accept='.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                beforeUpload={c.handleFileUpload}
                showUploadList={false}
              >
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined style={{ fontSize: '52px', color: 'var(--color-warning)' }} />
                </p>
                <p className='ant-upload-text'>上傳待比對文件</p>
                <p className='ant-upload-hint'>支援 Word（.docx）與 PDF 檔案，檔案上限為 10 MB</p>
              </Dragger>

              <div style={{ textAlign: 'center' }}>
                <Space size='middle' wrap style={{ justifyContent: 'center' }}>
                  <Button
                    icon={<DiffOutlined />}
                    onClick={() => setTrackChangesModalVisible(true)}
                    size='large'
                    type='primary'
                    style={{
                      fontSize: '16px',
                      height: '48px',
                      paddingLeft: '20px',
                      paddingRight: '20px',
                    }}
                  >
                    修訂記錄
                  </Button>
                  <Button
                    icon={<HistoryOutlined />}
                    onClick={c.showHistory}
                    size='large'
                    type='primary'
                    style={{
                      fontSize: '16px',
                      height: '48px',
                      paddingLeft: '20px',
                      paddingRight: '20px',
                    }}
                  >
                    歷史記錄
                  </Button>
                </Space>
              </div>
            </>
          )}

          {/* 操作按鈕區域 */}
          {(c.uploadedFile || c.pdfData || c.isReviewing) && (
            <Card>
              <div style={{ marginBottom: '12px' }}>
                <RuleSelector
                  compact
                  ruleSets={c.ruleSets}
                  selectedRuleSetId={c.ruleSetId}
                  onRuleSetChange={c.setRuleSetId}
                  selectedValue={c.selectRuleValue}
                  onValueChange={c.handleRuleSelectChange}
                  onUploadClick={c.openUploadRuleModal}
                  onShareClick={c.openShareModal}
                  onPreviewClick={c.openPreviewModal}
                  onRefresh={c.loadRuleSets}
                  isLoading={c.isLoadingRules}
                  isComparisonPage
                  userPreferredRuleSetId={c.user?.preferred_rule_set_id ?? null}
                  onSetUserPreferred={c.handleSetUserPreferred}
                  historyRuleSetName={c.historyRuleSetName}
                />
              </div>

              <div style={{ textAlign: 'center' }}>
                <Space wrap>
                  <Button
                    type='primary'
                    size='large'
                    onClick={c.handleReview}
                    loading={c.isReviewing}
                    disabled={c.isReviewing}
                    style={{ height: '40px' }}
                  >
                    {c.isReviewing ? '比對中...' : '開始比對'}
                  </Button>

                  <Button
                    icon={<HistoryOutlined />}
                    size='large'
                    onClick={c.showHistory}
                    style={{ height: '40px' }}
                  >
                    歷史記錄
                  </Button>

                  <Button
                    icon={<SaveOutlined />}
                    size='large'
                    onClick={c.handleSaveReview}
                    disabled={c.activeResults.length === 0}
                    type='default'
                    style={{ height: '40px', borderColor: '#52c41a', color: '#52c41a' }}
                    title='保存比對記錄到資料庫'
                  >
                    保存記錄
                  </Button>

                  <Button
                    icon={<DownloadOutlined />}
                    size='large'
                    onClick={c.handleDownloadWordReport}
                    disabled={c.activeResults.length === 0}
                    style={{ height: '40px' }}
                    title='下載附評論的 Word 報告'
                  >
                    Word 報告
                  </Button>

                  <Button
                    icon={<DownloadOutlined />}
                    size='large'
                    onClick={handleDownloadTrackChanges}
                    loading={isDownloadingTrackChanges}
                    disabled={c.activeResults.length === 0 || !c.ruleSetId}
                    style={{ height: '40px' }}
                    title='以原稿為基底，產出帶有修訂記錄的 Word 文件'
                  >
                    修訂記錄
                  </Button>

                  <Button type='text' onClick={c.resetUpload} title='重新上傳文件'>
                    🔄 重新上傳
                  </Button>
                </Space>

                {(c.uploadedFile || c.isReviewing) && (
                  <div style={{ marginTop: '12px' }}>
                    <span style={{ color: 'var(--color-success)', fontSize: '14px' }}>
                      ✓ 已上傳：{c.uploadedFile?.name ?? '（恢復中...）'}
                    </span>
                  </div>
                )}

                {c.isReviewing && (
                  <div
                    style={{
                      marginTop: '20px',
                      padding: '16px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '8px',
                        color: '#1E88A8',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {c.reviewStage}
                    </div>
                    <Progress
                      percent={c.reviewProgress}
                      strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                      status={c.reviewProgress === 100 ? 'success' : 'active'}
                      size='default'
                    />
                    {c.connectionWarning && (
                      <div
                        style={{
                          marginTop: '6px',
                          color: '#faad14',
                          fontSize: '12px',
                          textAlign: 'center',
                        }}
                      >
                        ⚠️ 網路連線不穩定，系統仍在嘗試重新連線...
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Main Content */}
          {(c.pdfData || c.isLoadingHistoryFile || c.reviewResults.length > 0 || c.isReviewing) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: '24px',
                minHeight: '600px',
              }}
            >
              {c.pdfData ? (
                <Card
                  title={
                    <span>
                      <span style={{ marginRight: '8px' }}>📋</span>
                      原始文件內容
                    </span>
                  }
                  styles={{
                    body: {
                      height: REVIEW_PANEL_HEIGHT,
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                >
                  <PDFViewer
                    pdfData={c.pdfData}
                    reviewResults={c.activeResults}
                    onScaleChange={handlePdfScaleChange}
                  />
                </Card>
              ) : c.isLoadingHistoryFile || c.isReviewing ? (
                <Card
                  title={
                    <span>
                      <span style={{ marginRight: '8px' }}>📋</span>
                      原始文件內容
                    </span>
                  }
                  styles={{
                    body: {
                      height: REVIEW_PANEL_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                >
                  <Spin size='large' tip={c.isReviewing ? 'AI 比對中...' : '載入文件中...'} />
                </Card>
              ) : null}

              <Card
                title={
                  <span>
                    <span style={{ marginRight: '8px' }}>⚖️</span>
                    AI 比對結果
                  </span>
                }
                style={{
                  gridColumn:
                    c.pdfData || c.isLoadingHistoryFile || c.isReviewing ? 'auto' : '1 / -1',
                }}
                styles={{
                  body: {
                    height: REVIEW_PANEL_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  },
                }}
              >
                {!c.pdfData && !c.isLoadingHistoryFile && c.reviewResults.length > 0 && (
                  <div
                    style={{
                      padding: '12px 16px',
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      color: '#ad6800',
                      flexShrink: 0,
                    }}
                  >
                    <strong>📝 提示：</strong>
                    此記錄沒有原始檔案預覽。如需查看原始文件，請重新上傳檔案。
                  </div>
                )}

                {c.reviewResults.length > 0 && (
                  <div style={{ flexShrink: 0 }}>
                    <DisclaimerBanner />
                  </div>
                )}

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    marginTop: c.reviewResults.length > 0 ? 4 : 0,
                    overflow: 'auto',
                  }}
                >
                  <ReviewResults
                    results={c.activeResults}
                    isLoading={c.isReviewing}
                    fileName={c.uploadedFile?.name}
                    showRiskLevel={riskLevelEnabled}
                    hideSuggestionLabel
                  />
                </div>
              </Card>
            </div>
          )}
        </Space>
      </Content>

      {/* 歷史 */}
      <ReviewHistoryWithShare
        visible={c.historyVisible}
        onClose={() => c.setHistoryVisible(false)}
        onLoadHistory={c.loadHistoryDetail}
        recordType='comparison'
      />

      {/* 規則上傳（比對模式） */}
      <RuleUploadModal
        visible={c.uploadRuleModalVisible}
        onClose={c.closeUploadRuleModal}
        onSuccess={c.loadRuleSets}
        isAdmin={c.isAdmin}
        showAiMode={c.canUploadAiRuleset}
        isComparisonMode
      />

      {/* 部門指派 */}
      <DepartmentAssignModal
        visible={c.shareModalVisible}
        ruleSet={c.selectedRuleForShare}
        onClose={c.closeShareModal}
      />

      {/* 規則預覽 */}
      <RulePreviewModal
        visible={c.previewModalVisible}
        ruleSet={c.selectedRuleForPreview}
        onClose={c.closePreviewModal}
        onRuleSetNameChange={c.handleRuleSetNameChange}
        isComparisonPage
      />

      {/* 純修訂記錄 Modal */}
      <Modal
        title='產出修訂記錄'
        open={trackChangesModalVisible}
        onCancel={() => {
          setTrackChangesModalVisible(false);
          setTcOriginalFile(null);
          setTcModifiedFile(null);
        }}
        footer={
          <Button
            type='primary'
            icon={<DownloadOutlined />}
            loading={isGeneratingTrackChanges}
            disabled={!tcOriginalFile || !tcModifiedFile}
            onClick={handleGenerateTrackChanges}
          >
            產出修訂記錄
          </Button>
        }
        width={520}
      >
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          上傳原稿與修改版，系統將比對差異並產出帶有 Word 修訂記錄的文件（不含 AI 審閱）。
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {([
            { file: tcOriginalFile, setFile: setTcOriginalFile, label: '上傳原稿', color: '#1890ff' },
            { file: tcModifiedFile, setFile: setTcModifiedFile, label: '上傳修改版', color: '#52c41a' },
          ] as const).map(({ file, setFile, label, color }) => (
            <div key={label}>
              {file ? (
                <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', padding: '10px 12px', textAlign: 'center', background: '#f6ffed', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <FileWordOutlined style={{ fontSize: '20px', color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: 500, wordBreak: 'break-all', flex: 1, textAlign: 'left' }}>{file.name}</span>
                  <Button type='link' size='small' style={{ flexShrink: 0, padding: 0 }} onClick={() => setFile(null)}>重新選擇</Button>
                </div>
              ) : (
                <Upload.Dragger
                  accept='.docx,.pdf'
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={(f) => { setFile(f); return false; }}
                  style={{ padding: '8px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    <FileWordOutlined style={{ fontSize: '20px', color }} />
                    <span style={{ fontSize: '14px' }}>{label}</span>
                  </div>
                </Upload.Dragger>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </PageLayout>
  );
};

export default ContractComparisonPage;
